import { useRouter } from "@app/app/components/router/RouterContext";
import { useAppState, useBookingEngineStateContext, useCustomNavigate, usePropertyContext } from "@app/app/hooks";
import { tracker } from "@app/app/services/tracker";
import { IBookingEngineState } from "@app/app/types";
import { useCheckoutSessionContext } from "@app/checkout/hooks";
import { DateRangeSelector } from "@app/common/components/calendar/DateRangeSelector";
import { ControlledFormField } from "@app/common/components/form/ControlledFormField";
import { CheckoutActivityLog } from "@app/common/services/activityLog/CheckoutActivityLog";
import { Button } from "@app/componentLibrary/ui/button";
import { Flex } from "@app/componentLibrary/ui/flex";
import { ThemeTitle } from "@app/componentLibrary/ui/text";
import { PencilIcon } from "@heroicons/react/24/solid";
import { LogLevel } from "@hotelchamp/common";
import { Fragment, ReactElement, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { RoomsConfiguratorSection } from "./RoomsConfiguratorSection";

export interface ISearchConfiguratorProps {
    children?: ReactElement | ReactElement[];
    onSearchClick: () => void;
    showChangePropertyButton?: boolean;
}

export function SearchConfigurator({ children, onSearchClick, showChangePropertyButton = true }: ISearchConfiguratorProps) {
    const { t } = useTranslation(["availability", "common"]);
    const { propertyGroup, activeProperty, activePropertyId } = usePropertyContext();
    const { getState, calendarAvailability, setActiveCalendarDate, isLoading, resetBooking } = useBookingEngineStateContext();
    const { control } = useFormContext<IBookingEngineState>();
    const { bookingState } = getState();
    const { dates } = bookingState || {};
    const { fromDate, toDate } = dates || {};
    const availableDates = calendarAvailability;
    const invalidDateSelection = !fromDate || !toDate || !availableDates.hasOwnProperty(fromDate);
    const navigate = useCustomNavigate();
    const { routerData } = useRouter();
    const isChain = (propertyGroup?.properties || []).length > 1;
    const { globalState } = useAppState();
    const { state: checkoutSessionState, checkoutBookingValidation } = useCheckoutSessionContext();

    const handleSearchClick = () => {
        const nextQuery = getState()?.bookingState;

        tracker.searchQuery({ query: nextQuery });

        new CheckoutActivityLog({
            message: "Search selection confirmed",
            visitorId: globalState.visitorId,
            action: "handle_search_click",
            checkoutSessionId: checkoutSessionState.session_id,
            bookingId: checkoutSessionState.booking_id,
            channelBookingId: checkoutBookingValidation?.data?.reservedBooking?.reservation_id,
            propertyId: activePropertyId,
            metadata: {
                query: {
                    ...(nextQuery || {}),
                    bookings: (nextQuery?.bookings || []).map((b) => ({
                        ...b,
                        room: "stripped",
                        rate: "stripped",
                        extras: "stripped",
                    })),
                },
            },
        }).queue(LogLevel.Info);

        onSearchClick();
    };

    useEffect(() => {
        if (!availableDates.hasOwnProperty(fromDate) && !availableDates.hasOwnProperty(toDate) && !isLoading) {
            resetBooking();
        }
    }, [fromDate, toDate]);

    const handleCalendarActiveDateChange = (activeDate: Date) => {
        setActiveCalendarDate(activeDate);
    };

    const handleEditClick = () => {
        navigate(routerData.back);
    };

    return (
        <Fragment>
            <div className="grow overflow-auto w-full h-auto px-5 pt-6 pb-4" data-scrollable>
                <Fragment>
                    <div className="flex flex-row justify-between mb-4">
                        {isChain && (
                            <ThemeTitle size="xxl" className="flex-auto items-center self-center" data-testid="search-property-name">
                                {activeProperty?.name}
                            </ThemeTitle>
                        )}
                        {isChain && !!showChangePropertyButton && (
                            <Button
                                variant="ghost"
                                className="text-gray-600 font-normal hover:bg-white hover:border-white pr-1 decoration-gray-600"
                                icon={PencilIcon}
                                iconPosition="right"
                                onClick={handleEditClick}
                            >
                                <span className="hover:underline underline-offset-4">{t("change", { ns: "common" })}</span>
                            </Button>
                        )}
                    </div>
                    <div data-testid="calendar" className="mb-4">
                        <ControlledFormField
                            name="bookingState.dates"
                            control={control}
                            availability={availableDates}
                            isLoading={isLoading}
                            component={DateRangeSelector}
                            required={true}
                            onActiveDateChange={handleCalendarActiveDateChange}
                        />
                    </div>

                    {children}

                    <RoomsConfiguratorSection
                        propertyId={activeProperty?.id || ""}
                        property={activeProperty}
                        control={control}
                        bookingState={bookingState}
                    />
                </Fragment>
            </div>
            <Flex
                justifyContent="center"
                className="py-3 border-t border-gray-200 px-5"
                style={{ boxShadow: "0px -2px 4px 0px rgba(101, 101, 101, 0.01), 0px -4px 8px 0px rgba(101, 101, 101, 0.04)" }}
            >
                <Button
                    data-testid="search-btn"
                    variant="default"
                    className="w-full"
                    disabled={invalidDateSelection || isLoading}
                    onClick={handleSearchClick}
                    isLoading={isLoading}
                    loadingText=""
                >
                    {t("search.search")}
                </Button>
            </Flex>
        </Fragment>
    );
}
